// src/encounter/lib/import-bundle.ts
// Merge a saved BUNDLE into the live A/P canvas (PROD-1839).
//
// Flutter: `importBundle` (patient_encounter_bloc.dart:3382-3460). Three behaviours are ported
// deliberately, because each one is easy to get subtly wrong:
//
//  1. DEDUPE IS BY CODE, NOT BY DISPLAY TEXT. Flutter matches an incoming condition against the
//     existing plans on `element.description` — and `description` is a GETTER returning
//     `conditionCode!.code` (patient_assessment_model.dart:835-838), NOT the human label. Reading it
//     as "dedupe by name" would merge two genuinely different diagnoses that happen to share a
//     display string, and split one that doesn't. This mirrors handleAddDx's own code-level dedupe.
//
//  2. AN ALREADY-PRESENT DIAGNOSIS IS KEPT, NOT REPLACED. Flutter looks up the existing plan and
//     merges the bundle's child orders INTO it, excluding any whose description already exists there,
//     then re-appends the existing ones. The clinician's own edits win; the bundle only ever adds.
//
//  3. IDS ARE NOT CARRIED OVER. A bundle is persisted through `toBundleConditionJson`
//     (patient_assessment_model.dart:795-813), which emits no `identifier`, `encounter`, `subject` or
//     `performer` — a bundle is stored stripped of encounter identity. Anything this function
//     produces is therefore a NEW row for this encounter, and every id it mints is synthetic so
//     `collectServerIds` never adopts it and the save creates fresh children. Re-keying has to run
//     across all four structures at once (diagnoses, orders' `dxIds`, the payload map and the coding
//     map are all keyed by those ids), which is the reason this is one function and not four.
import { SYNTHETIC_ID_PREFIX, stampNewOrderDates, withoutServerDates } from './order-payload';
import { stampNewArticle } from './to-education-article';
import type { PatientAssessment } from '@/types/assessment-api';
import type { OrderPayloadMap, DxCodingMap } from './order-payload';
import type { PatientAssessmentCanvasState } from './from-patient-assessment';


/**
 * Give every condition in a BUNDLE a unique identifier before it is mapped to canvas state.
 *
 * WITHOUT THIS, IMPORTING A BUNDLE SILENTLY LOSES EVERY ORDER (reported 2026-08-08). A bundle is
 * persisted through `toBundleConditionJson`, which deliberately emits no `identifier`
 * (patient_assessment_model.dart:795-813) — correct for a template, fatal for the loader.
 * `patientAssessmentToCanvas` keys diagnoses by that identifier and links each nested order to its
 * parent through the same value, so a bundle arrives with EVERY condition sharing `id: ""`:
 *
 *   DX      [{id:"", code:"E11.9"}, {id:"", code:"I10"}]
 *   ORDERS  [{id:"local::-rad-0", dxIds:[""]}]      <- two conditions' orders collapsed into one
 *
 * The collision then makes the merge drop the survivor as unlinkable, so the user sees the
 * diagnoses appear and the orders never arrive. Stamping a per-index identifier restores the
 * one-to-one parent link. The ids are local to this mapping pass and are replaced by synthetic ids
 * in the merge below, so nothing derived from them reaches the encounter.
 */
export function withBundleConditionIds(pa: PatientAssessment): PatientAssessment {
  return {
    ...pa,
    assessmentPlanCondition: (pa.assessmentPlanCondition ?? []).map((c, i) => ({
      ...(c as unknown as Record<string, unknown>),
      identifier: (c as unknown as { identifier?: string }).identifier || `bundle-dx-${i}`,
    })) as unknown as PatientAssessment['assessmentPlanCondition'],
  };
}

/** Just the canvas slices an import touches — keeps this callable from a test without a whole page. */
export interface ImportableCanvas {
  diagnoses: PatientAssessmentCanvasState['diagnoses'];
  orders: PatientAssessmentCanvasState['orders'];
  payloads: OrderPayloadMap;
  dxCoding: DxCodingMap;
  articles: PatientAssessmentCanvasState['articles'];
}

export interface ImportBundleResult extends ImportableCanvas {
  /** Diagnoses actually added (already-present ones are skipped, per Flutter). */
  addedDx: number;
  /** Diagnoses refused because the canvas is at the cap. */
  skippedForCap: number;
  /**
   * Orders dropped because every diagnosis justifying them was refused by the cap. Reported so the
   * caller can SAY SO — a provider who built the bundle expecting those orders would otherwise get
   * no feedback at all (review, PR #292).
   */
  droppedOrders: number;
  /** True when nothing at all changed — the caller says so rather than looking broken. */
  noop: boolean;
}

const codeKey = (c: string | null | undefined) => (c ?? '').trim().toLowerCase();

/**
 * Drop encounter identity from a row copied out of a bundle.
 *
 * A spread (`{...dx}`) is NOT safe here, and a review of PR #292 caught it: a bundle saved before
 * `toBundleAssessment` existed — or written by any other client — can still carry a previous
 * patient's `subject`/`encounter`/`identifier` on its rows, and a blind spread would carry those
 * onto THIS encounter's canvas, where the save could echo them back. Nothing downstream reads these
 * keys, which is exactly why the leak would be silent. Strip at the boundary instead of trusting
 * the payload.
 */
function withoutEncounterIdentity<T>(row: T): T {
  const {
    subject: _s,
    encounter: _e,
    performer: _p,
    identifier: _i,
    ...rest
  } = row as T & {
    subject?: unknown;
    encounter?: unknown;
    performer?: unknown;
    identifier?: unknown;
  };
  return rest as T;
}

/**
 * Merge `incoming` (a bundle mapped through `patientAssessmentToCanvas`) into `current`.
 *
 * Pure: returns new collections and mutates nothing, so the caller can drop the result straight into
 * setState. `maxDiagnoses` mirrors Flutter's AppConstants.maxDiagnosisInAssessment (20).
 */
export function importBundleIntoCanvas(
  current: ImportableCanvas,
  incoming: ImportableCanvas,
  maxDiagnoses: number,
): ImportBundleResult {
  const existingCodes = new Set(current.diagnoses.map((d) => codeKey(d.code)).filter(Boolean));

  const diagnoses = [...current.diagnoses];
  const orders = [...current.orders];
  const payloads: OrderPayloadMap = new Map(current.payloads);
  const dxCoding: DxCodingMap = new Map(current.dxCoding);

  // Old bundle-local id → the synthetic id this encounter will use. Built as diagnoses are accepted
  // so the order pass below can rewrite `dxIds` without a second lookup pass.
  const dxIdMap = new Map<string, string>();
  let seq = 0;
  // One clock read for the whole import: everything it adds is added now (PROD-2854).
  const now = new Date().toISOString();
  const mintId = (kind: string) => `${SYNTHETIC_ID_PREFIX}bundle-${kind}-${Date.now()}-${seq++}`;

  let addedDx = 0;
  let skippedForCap = 0;
  let droppedOrders = 0;

  for (const dx of incoming.diagnoses) {
    const key = codeKey(dx.code);
    // Already on the canvas → keep the clinician's row untouched, but still route this bundle's
    // orders to it (behaviour 2 above).
    if (key && existingCodes.has(key)) {
      const existing = diagnoses.find((d) => codeKey(d.code) === key);
      if (existing?.id && dx.id) dxIdMap.set(dx.id, existing.id);
      continue;
    }
    if (diagnoses.length >= maxDiagnoses) {
      skippedForCap += 1;
      continue;
    }
    const newId = mintId('dx');
    if (dx.id) dxIdMap.set(dx.id, newId);
    if (key) existingCodes.add(key);
    // `endDate` is the stored row's, not this visit's (PROD-2854) — an imported Dx is new here.
    const dxRow = { ...withoutEncounterIdentity(dx) };
    delete dxRow.endDate;
    diagnoses.push({
      ...dxRow,
      id: newId,
      // The canvas already has a primary unless it was empty — an imported dx must never steal it.
      isPrimary: diagnoses.length === 0,
      onSetDate: now,
    });
    if (dx.id) {
      const coding = incoming.dxCoding.get(dx.id);
      if (coding) dxCoding.set(newId, coding);
    }
    addedDx += 1;
  }

  // Orders whose every justifying dx was dropped (cap) would dangle, so they are dropped too.
  const existingOrderKeys = new Set(
    current.orders.map((o) => `${o.type}::${codeKey(o.label)}`),
  );
  for (const order of incoming.orders) {
    const mappedDxIds = (order.dxIds ?? []).map((id) => dxIdMap.get(id)).filter((v): v is string => !!v);
    if ((order.dxIds ?? []).length > 0 && mappedDxIds.length === 0) {
      droppedOrders += 1;
      continue;
    }
    // Flutter excludes a child order whose description already exists on the matching plan.
    const key = `${order.type}::${codeKey(order.label)}`;
    if (existingOrderKeys.has(key)) continue;
    existingOrderKeys.add(key);

    const newId = mintId('order');
    orders.push({ ...withoutEncounterIdentity(order), id: newId, dxIds: mappedDxIds });
    if (order.id) {
      const payload = incoming.payloads.get(order.id);
      // A NEW order on this visit (PROD-2854): drop any date the stored row carried (Flutter's bundle
      // toJson writes them), then stamp the import time, so every later save sends that same date.
      if (payload) payloads.set(newId, stampNewOrderDates(withoutServerDates(payload), now));
    }
  }

  const articles = [...current.articles];
  const seenArticles = new Set(current.articles.map((a) => codeKey(a.display)));
  for (const a of incoming.articles) {
    const key = codeKey(a.display);
    if (key && seenArticles.has(key)) continue;
    seenArticles.add(key);
    // New on this visit too (PROD-2854): the import time, not the bundle's stored dates.
    const article = { ...withoutEncounterIdentity(a) };
    delete article.onSetDate;
    delete article.endDate;
    articles.push(stampNewArticle(article, now));
  }

  return {
    diagnoses,
    orders,
    payloads,
    dxCoding,
    articles,
    addedDx,
    skippedForCap,
    droppedOrders,
    noop:
      diagnoses.length === current.diagnoses.length &&
      orders.length === current.orders.length &&
      articles.length === current.articles.length,
  };
}
